import { HEN_BREEDS } from '../data/breeds';
import { OFFLINE_EFFICIENCY, OFFLINE_MIN_MS } from '../data/balance';
import type { BreedId } from '../data/types';
import { earn, farmTotals, henStats, nestCapacity, offlineCapMs } from './economy';
import { logActivity } from './state';
import type { GameState, SimCtx } from './types';

/**
 * Offline progress (Act 2 core). While away the world is paused — no events,
 * no broody cycles — but gatherers keep collecting at the steady passive
 * rate, at OFFLINE_EFFICIENCY, up to the per-rooster cap. The accelerator
 * bank (earned by click-combos) stretches the credited time up to 2×.
 */

export interface OfflineReport {
  awayMs: number;
  creditedMs: number;
  drainedMs: number;
  capMs: number;
  capped: boolean;
  earned: number;
  eggs: number;
  perFlock: { flockId: number; name: string; earned: number; topBreed: BreedId | null }[];
  perBreed: { breed: BreedId; earned: number }[];
  noGathering: boolean;
}

export function applyOffline(s: GameState, ctx: SimCtx, awayMs: number): OfflineReport | null {
  if (!(awayMs >= OFFLINE_MIN_MS)) return null;
  const capMs = offlineCapMs(s, ctx);
  const drained = Math.min(s.acceleratorBank, awayMs, Math.max(0, capMs - awayMs));
  s.acceleratorBank -= drained;
  const creditedMs = Math.min(awayMs + drained, capMs);
  const t = farmTotals(s, ctx, true);
  const secs = creditedMs / 1000;

  // Pro-rate the passive income by each hen's share (for the welcome-back breakdown).
  const perFlock: OfflineReport['perFlock'] = [];
  const breedTotals = new Map<BreedId, number>();
  let total = 0;
  for (const f of s.flocks) {
    let flockEarned = 0;
    let top: { breed: BreedId; v: number } | null = null;
    for (const b of f.boxes) {
      if (!b.hen) continue;
      const st = henStats(s, ctx, f, b.hen, true);
      const v = st.layRate * st.eggValue * st.autoMult * t.efficiency * OFFLINE_EFFICIENCY * secs;
      flockEarned += v;
      breedTotals.set(b.hen.breed, (breedTotals.get(b.hen.breed) ?? 0) + v);
      if (!top || v > top.v) top = { breed: b.hen.breed, v };
    }
    total += flockEarned;
    perFlock.push({ flockId: f.id, name: f.name, earned: flockEarned, topBreed: top?.breed ?? null });
  }
  const earned = earn(s, total);
  const eggs = Math.floor(Math.min(t.layRate, t.gatherRate) * secs * OFFLINE_EFFICIENCY);
  s.eggsCollected += eggs;
  s.eggsLaid += Math.floor(t.layRate * secs);

  // Anything the gatherers couldn't keep up with piles up in the nests.
  for (const f of s.flocks) {
    for (const b of f.boxes) {
      if (!b.hen || b.hen.broodySince !== null) continue;
      const st = henStats(s, ctx, f, b.hen, true);
      const laid = st.layRate * secs * (1 - t.efficiency);
      const cap = nestCapacity(b);
      if (laid >= 1 && b.eggs < cap) {
        b.eggs = Math.min(cap, b.eggs + Math.floor(laid));
        b.oldestEggAt ??= s.time;
      }
    }
  }

  const perBreed = [...breedTotals.entries()].map(([breed, e]) => ({ breed, earned: e })).sort((a, b) => b.earned - a.earned);
  if (earned > 0) {
    logActivity(s, 'milestone', 'Welcome back', `Earned $${Math.floor(earned).toLocaleString('en-US')} while away`);
  }
  return {
    awayMs, creditedMs, drainedMs: drained, capMs, capped: awayMs + drained > capMs, earned, eggs,
    perFlock, perBreed, noGathering: t.gatherRate <= 0,
  };
}

export function breedName(b: BreedId): string {
  return HEN_BREEDS[b].name;
}
